/**
 * Runtime Environment Configuration
 * 
 * Reads configuration from window.__ENV__ which is populated at container startup.
 * Falls back to process.env for local development (build-time).
 * 
 * This enables K8s-native configuration without rebuilding the image.
 */

// Extend Window interface to include __ENV__
declare global {
  interface Window {
    __ENV__?: {
      API_URL?: string;
      API_KEY?: string;
      CLERK_PUBLISHABLE_KEY?: string;
      CLERK_TOKEN_TEMPLATE?: string;
      STRIPE_PUBLISHABLE_KEY?: string;
      ENABLE_BILLING?: string;
      ENABLE_AUTH?: string;
      USE_MOCK_API?: string;
      UMAMI_WEBSITE_ID?: string;
      UMAMI_HOST?: string;
      USER_ANALYTICS_ENABLED?: string;
      USER_ANALYTICS_API_URL?: string;
      USER_ANALYTICS_API_KEY?: string;
      USER_ANALYTICS_TRACK_PATH?: string;
      LIVE_DATA_ENABLED?: string;
      DEMO_DATA_END?: string;
    };
  }
}

/**
 * Get config value from runtime env (K8s) or build-time env (local dev)
 */
function getEnv(key: string, fallback: string = ''): string {
  // First try runtime config (K8s/Docker)
  const runtimeValue = window.__ENV__?.[key as keyof typeof window.__ENV__];
  if (runtimeValue && !runtimeValue.startsWith('${')) {
    return runtimeValue;
  }
  
  // Fall back to build-time env via Vite (local development)
  // Vite exposes env vars via import.meta.env with VITE_ prefix
  const viteKey = `VITE_${key}`;
  const viteValue = (import.meta.env as Record<string, string>)[viteKey];
  if (viteValue) {
    return viteValue;
  }
  
  return fallback;
}

/**
 * Application configuration - use this instead of process.env
 */
export const config = {
  // API
  apiUrl: getEnv('API_URL', ''),
  apiKey: getEnv('API_KEY', ''),
  
  // Clerk Authentication
  clerkPublishableKey: getEnv('CLERK_PUBLISHABLE_KEY', ''),
  clerkTokenTemplate: getEnv('CLERK_TOKEN_TEMPLATE', ''),
  
  // Stripe Billing
  stripePublishableKey: getEnv('STRIPE_PUBLISHABLE_KEY', ''),
  
  // Feature flags
  enableBilling: getEnv('ENABLE_BILLING', 'false') === 'true',
  enableAuth: getEnv('ENABLE_AUTH', 'true') === 'true',
  
  // Mock API mode for frontend-only development
  useMockApi: getEnv('USE_MOCK_API', 'false') === 'true',

  // Umami analytics website ID — empty = disabled
  umamiWebsiteId: getEnv('UMAMI_WEBSITE_ID', ''),
  umamiHost: getEnv('UMAMI_HOST', ''),

  // User Analytics ingestion
  userAnalyticsEnabled: getEnv('USER_ANALYTICS_ENABLED', 'false') === 'true',
  userAnalyticsApiUrl: getEnv('USER_ANALYTICS_API_URL', 'https://analytics-api.mendllabs.ai'),
  userAnalyticsApiKey: getEnv('USER_ANALYTICS_API_KEY', ''),
  userAnalyticsTrackPath: getEnv('USER_ANALYTICS_TRACK_PATH', '/events/track'),

  // Set to 'true' once a live market-data provider (e.g. Tardis) is wired up
  // on the backtesting backend. When false, the GUI anchors all date pickers
  // to the end of bundled demo data so jobs land inside available coverage.
  liveDataEnabled: getEnv('LIVE_DATA_ENABLED', 'false') === 'true',
  // ISO date (YYYY-MM-DD) marking the last day covered by bundled demo data.
  demoDataEnd: getEnv('DEMO_DATA_END', '2025-12-31'),
};

/**
 * Check if Clerk auth is configured
 */
export const isClerkConfigured = (): boolean => {
  if (!config.enableAuth) {
    return false;
  }
  return !!config.clerkPublishableKey && config.clerkPublishableKey.startsWith('pk_');
};

/**
 * Check if we should use mock API data (no backend available)
 * Note: Check env directly to avoid module initialization timing issues
 */
export const useMockApi = (): boolean => {
  // Check runtime config first (K8s/Docker)
  const runtimeValue = window.__ENV__?.USE_MOCK_API;
  if (runtimeValue && !runtimeValue.startsWith('${')) {
    return runtimeValue === 'true';
  }
  
  // Check build-time env (Vite)
  const viteValue = import.meta.env.VITE_USE_MOCK_API;
  return viteValue === 'true';
};

/**
 * Check if Stripe is configured
 */
export const isStripeConfigured = (): boolean => {
  return !!config.stripePublishableKey && config.stripePublishableKey.startsWith('pk_');
};

/**
 * Whether the backend has a live market-data provider configured.
 * Controls whether GUI date pickers anchor to today (live) or the end
 * of bundled demo data (demo-only).
 */
export const isLiveDataEnabled = (): boolean => config.liveDataEnabled;

/**
 * Reference "now" date for backtest date-range calculations.
 * Returns today when live data is available, otherwise the last day
 * of bundled demo data so generated date ranges have actual coverage.
 */
export const getDataAnchorDate = (): Date => {
  if (isLiveDataEnabled()) {
    return new Date();
  }
  return new Date(`${config.demoDataEnd}T00:00:00Z`);
};

export default config;
